// fetching the data
// fetching the data while pagination
// loading state
// filters state
// sorting state
// configuration for the table
import React, { useEffect, useState, useContext } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _isFunction from 'lodash/isFunction';
import { CustomColumn, NumericalColumn } from 'baseui/data-table';

import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../constants';
import { addFilter, removeFilter } from '../../utils/filters';

import SingleRowAction from './components/SingleRowAction';
import { AppContext } from '../../context';

import { DataTableProps } from './types';

const DEFAULT_PAGE_DETAILS = {
	pageNo: 1,
	pageSize: 5000
};

const isBulkActionEnabled = (bulkActions: any) => !_isEmpty(bulkActions);

const useDataTable = ({
	fetchData,
	columnConfigs,
	getActionHandler,
	app,
	defaults = EMPTY_OBJECT,
	actions,
	entity,
	hideActions = false,
	globalForceUpdate,
	turnGlobalForceUpdateOff,
	isPreProd = false
}: DataTableProps) => {
	const { showAlert } = useContext(AppContext);
	const { rowActions, bulkActions } = actions;
	const [data, setData] = useState({ rowCount: 0, rows: [] });
	const [loading, setLoading] = useState(true);
	const [emptyMessage, setEmptyMessage] = useState();
	const [pageDetails, setPageDetails] = useState(defaults.pageDetails || DEFAULT_PAGE_DETAILS);
	const [sortDetails, setSortDetails] = useState(defaults.sortDetails || EMPTY_ARRAY);
	const [searchText, setSearchText] = useState('');
	const [selectedRows, setSelectedRows] = useState<{ id: any; rowData: any | never[] | undefined }[]>([]);
	const [forceUpdateRows, setForceUpdateRows] = useState(false);
	const [confirmationDialog, setConfirmationDialog] = useState<{
		show: boolean;
		message?: string;
		header?: string;
		handleAlertConfirmation?: () => void;
		handleAlertCancel?: () => void;
	}>({
		show: false
	});

	const fetchRowData = async () => {
		setLoading(true);
		try {
			const fetchedData = await fetchData({ pageDetails, sortDetails, app, searchText, isPreProd });
			const reqdData = fetchedData.data;

			if (fetchedData) {
				const reqdRows = reqdData.map((row: any) => ({ id: row.id, data: row }));
				setData({ rowCount: fetchedData.rowCount, rows: reqdRows });
				if (_isFunction(setForceUpdateRows)) {
					setForceUpdateRows(false);
				}
				setLoading(false);
			}
		} catch (error: any) {
			const errorMessage = _get(error, ['response', 'data', 'error'], 'Unable to fetch data');
			showAlert(errorMessage, { type: 'error' });
			setEmptyMessage(errorMessage);
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRowData();
	}, [pageDetails, app, searchText]);

	useEffect(() => {
		if (forceUpdateRows || globalForceUpdate) {
			fetchRowData();
			if (_isFunction(turnGlobalForceUpdateOff) && globalForceUpdate) {
				turnGlobalForceUpdateOff();
			}
		}
	}, [forceUpdateRows, globalForceUpdate]);

	const handleSortChange = (sort: any) => {
		setSortDetails(sort);
	};

	const handlePageChange = (page: any) => {
		setPageDetails(page);
	};

	const handleAddFilter = (filterKey: any, filterDesc: any) => {
		addFilter(entity, filterKey, filterDesc);
	};

	const handleRemoveFilter = (filterKey: any) => {
		removeFilter(entity, filterKey);
	};

	const onSelectionModelChange = ({ selectionModel }: { selectionModel: any }) => {
		const filterSelectedRows = _map(selectionModel, (rowId) => ({
			id: rowId,
			rowData: _find(data, (row: any) => row.id === rowId)
		}));
		setSelectedRows(filterSelectedRows);
	};

	const handleTextQueryChange = (query: string, fullDataSearch: boolean) => {
		if (fullDataSearch) {
			setSearchText(query);
			setPageDetails(defaults.pageDetails || DEFAULT_PAGE_DETAILS);
		}
	};

	const batchActions = _map(
		_filter(bulkActions, (action): any => _isFunction(action.isEnabled) && action.isEnabled()),
		(action) => {
			return {
				label: action.name,
				onClick: ({ selection, clearSelection }: any) => {
					getActionHandler(action, { setForceUpdateRows, selection })();
					clearSelection();
				}
			};
		}
	);

	const handleAlertConfirmation = () => {
		if (confirmationDialog.handleAlertConfirmation) {
			confirmationDialog.handleAlertConfirmation();
		}
		setConfirmationDialog({ show: false });
	};

	const handleAlertCancel = () => {
		if (confirmationDialog.handleAlertCancel) {
			confirmationDialog.handleAlertCancel();
		}
		setConfirmationDialog({ show: false });
	};
	return {
		rows: data.rows,
		loading,
		emptyMessage,
		confirmationDialog,
		columns: [
			NumericalColumn({
				title: 'Index',
				maxWidth: 100,
				minWidth: 100,
				mapDataToValue: (rowData: any) => rowData.index
			}),
			...(hideActions === false && !_isEmpty(rowActions)
				? [
						CustomColumn({
							title: 'ROW ACTIONS',
							mapDataToValue: (colData: any) => colData,
							minWidth: 80,
							maxWidth: 150,
							renderCell: ({ value }: { value: any }) => {
								const rowData = value;
								if (rowData.hideAction == false) return null;
								return (
									<SingleRowAction
										rowData={rowData}
										rowActions={rowActions}
										getActionHandler={getActionHandler}
										setConfirmationDialog={setConfirmationDialog}
										setForceUpdateRows={setForceUpdateRows}
									/>
								);
							}
						})
				  ]
				: []),

			...columnConfigs
		],
		handleSortChange,
		handlePageChange,
		handleAddFilter,
		handleRemoveFilter,
		checkboxSelection: isBulkActionEnabled(bulkActions),
		onSelectionModelChange,
		handleTextQueryChange,
		selectedRows,
		rowCount: data.rowCount,
		pageDetails,
		batchActions,
		handleAlertCancel,
		handleAlertConfirmation,
		setForceUpdateRows
	};
};

export default useDataTable;
